// frontend/src/pages/AdminPresenca.js
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus } from 'lucide-react';
import AgendaCalendar from '../components/agenda/AgendaCalendar';
import AgendaSidebar from '../components/agenda/AgendaSidebar';
import SessionModal from '../components/agenda/SessionModal';
import AttendanceModal from '../components/agenda/AttendanceModal';
import StudentReportModal from '../components/agenda/StudentReportModal';

const BACKEND_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:8000'
  : (process.env.REACT_APP_BACKEND_URL || '');

export default function AdminPresenca() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('admin_token');

  const [agendaStudents, setAgendaStudents] = useState([]);
  const [sessionTypes, setSessionTypes] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [calendarRange, setCalendarRange] = useState(null);
  const [attendanceStats, setAttendanceStats] = useState({}); // { studentId: { total, present, pct } }

  const [sessionModal, setSessionModal] = useState(null);
  const [attendanceModal, setAttendanceModal] = useState(null);
  const [reportModal, setReportModal] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAttendanceStats = useCallback(async (students) => {
    if (!students.length) return;
    try {
      const results = await Promise.all(
        students.map(s =>
          axios.get(`${BACKEND_URL}/api/admin/agenda/attendance/student/${s.id}`, { headers })
            .then(r => ({ id: s.id, records: r.data }))
            .catch(() => ({ id: s.id, records: [] }))
        )
      );
      const stats = {};
      for (const { id, records } of results) {
        const total = records.length;
        const present = records.filter(r => r.status === 'present').length;
        stats[id] = { total, present, pct: total > 0 ? Math.round((present / total) * 100) : null };
      }
      setAttendanceStats(stats);
    } catch {}
  }, []); // eslint-disable-line

  const fetchStudents = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/agenda/students`, { headers });
      setAgendaStudents(res.data);
      fetchAttendanceStats(res.data);
    } catch {}
  }, [fetchAttendanceStats]); // eslint-disable-line

  const fetchSessionTypes = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/agenda/session-types`, { headers });
      setSessionTypes(res.data);
    } catch {}
  }, []); // eslint-disable-line

  const fetchSessions = useCallback(async (range) => {
    if (!range) return;
    try {
      const from = range.start.toISOString().slice(0, 10);
      const to   = range.end.toISOString().slice(0, 10);
      const res = await axios.get(`${BACKEND_URL}/api/admin/agenda/sessions?from=${from}&to=${to}`, { headers });
      setSessions(res.data);
    } catch {}
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!token) { navigate('/admin'); return; }
    fetchStudents();
    fetchSessionTypes();
  }, [token, navigate, fetchStudents, fetchSessionTypes]);

  useEffect(() => {
    fetchSessions(calendarRange);
  }, [calendarRange, fetchSessions]);

  const handleRangeChange = useCallback((range) => setCalendarRange(range), []);

  const handleSlotSelect = useCallback((date) => {
    setSessionModal({ mode: 'create', date });
  }, []);

  const handleSessionClick = useCallback((session, date) => {
    const isPast = new Date(date) < new Date();
    if (isPast) {
      setAttendanceModal({ session, date });
    } else {
      setSessionModal({ mode: 'edit', session, date });
    }
  }, []);

  const handleSessionSaved = () => {
    fetchSessions(calendarRange);
    setSessionModal(null);
  };

  const handleAttendanceSaved = () => {
    fetchSessions(calendarRange);
    setAttendanceModal(null);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-6 pb-20">
      <div className="max-w-7xl mx-auto px-4 md:px-8">

        <div className="mb-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin')} className="text-[#555] hover:text-white transition-colors">
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-xl font-bold tracking-tight uppercase">Presença</h1>
          </div>
          <button
            onClick={() => setSessionModal({ mode: 'create', date: new Date() })}
            className="flex items-center gap-2 px-4 py-2.5 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-[#E5E5E5] transition-colors"
          >
            <Plus size={14} /> Nova sessão
          </button>
        </div>

        <div className="flex gap-6">
          <div className="flex-1 min-w-0">
            <AgendaCalendar
              sessions={sessions}
              sessionTypes={sessionTypes}
              agendaStudents={agendaStudents.filter(s => s.active)}
              onRangeChange={handleRangeChange}
              onSlotSelect={handleSlotSelect}
              onSessionClick={handleSessionClick}
            />
          </div>
          <div className="w-60 flex-shrink-0">
            <AgendaSidebar
              agendaStudents={agendaStudents}
              sessions={sessions}
              attendanceStats={attendanceStats}
              onStudentClick={(s) => setReportModal(s)}
              onAddStudent={fetchStudents}
              token={token}
              backendUrl={BACKEND_URL}
            />
          </div>
        </div>
      </div>

      {sessionModal && (
        <SessionModal
          mode={sessionModal.mode}
          initialDate={sessionModal.date}
          session={sessionModal.session}
          sessionTypes={sessionTypes}
          agendaStudents={agendaStudents}
          token={token}
          backendUrl={BACKEND_URL}
          onSaved={handleSessionSaved}
          onClose={() => setSessionModal(null)}
          onSessionTypesChange={fetchSessionTypes}
        />
      )}
      {attendanceModal && (
        <AttendanceModal
          session={attendanceModal.session}
          date={attendanceModal.date}
          sessionTypes={sessionTypes}
          agendaStudents={agendaStudents}
          token={token}
          backendUrl={BACKEND_URL}
          onSaved={handleAttendanceSaved}
          onClose={() => setAttendanceModal(null)}
        />
      )}
      {reportModal && (
        <StudentReportModal
          student={reportModal}
          token={token}
          backendUrl={BACKEND_URL}
          sessionTypes={sessionTypes}
          sessions={sessions}
          onClose={() => setReportModal(null)}
          onStudentUpdate={() => { fetchStudents(); setReportModal(null); }}
        />
      )}
    </div>
  );
}
